/**
 * In-game bug reports. F8 (or the "!" touch button) opens a text box; the report is POSTed
 * as JSON to the server's HTTP endpoint (`http://<server>:<port>/bug`, the same port as the
 * websocket — see server/index.ts, `--bug-dir`). With no server reachable (single player
 * without a LAN server) the report is kept in localStorage and can be exported later.
 */
import type { RenderState } from './render-state';

const recentErrors: string[] = [];
window.addEventListener('error', (e) => { recentErrors.push(`${new Date().toISOString()} ${e.message} @${e.filename}:${e.lineno}`); if (recentErrors.length > 10) recentErrors.shift(); });
window.addEventListener('unhandledrejection', (e) => { recentErrors.push(`${new Date().toISOString()} rejection: ${String(e.reason)}`); if (recentErrors.length > 10) recentErrors.shift(); });

export interface BugReport {
  text: string;
  /** what the player meant it as: something broken, or something they would like (owner, Sep 8) */
  kind: 'bug' | 'idea';
  at: string;
  name: string;
  mode: 'local' | 'net';
  server?: string;
  seed: number;
  pos: { x: number; y: number; z: number };
  vehicle?: { id: string; x: number; y: number; alt: number; speed: number; wrecked: boolean };
  hour: number;
  fps: number;
  zoom: number;
  screen: { w: number; h: number; dpr: number; touch: boolean };
  ua: string;
  version: string;
  errors: string[];
}

export function buildReport(text: string, state: RenderState, opts: { kind?: 'bug' | 'idea'; name: string; mode: 'local' | 'net'; server?: string; seed: number; fps: number; zoom: number; touch: boolean; version: string }): BugReport {
  const p = state.player;
  return {
    text, kind: opts.kind ?? 'bug', at: new Date().toISOString(), name: opts.name, mode: opts.mode, server: opts.server, seed: opts.seed,
    pos: { x: +p.x.toFixed(1), y: +p.y.toFixed(1), z: +p.z.toFixed(1) },
    vehicle: p.vehicle ? { id: p.vehicle.def.id, x: +p.vehicle.x.toFixed(1), y: +p.vehicle.y.toFixed(1), alt: +p.vehicle.alt.toFixed(1), speed: +p.vehicle.speed.toFixed(1), wrecked: p.vehicle.wrecked } : undefined,
    hour: +state.hour.toFixed(2), fps: Math.round(opts.fps), zoom: +opts.zoom.toFixed(2),
    screen: { w: innerWidth, h: innerHeight, dpr: devicePixelRatio, touch: opts.touch },
    ua: navigator.userAgent, version: opts.version, errors: [...recentErrors],
  };
}

/** ws://host:port → http://host:port/bug */
export function bugUrlFor(serverUrl: string): string {
  try { const u = new URL(serverUrl); return `${u.protocol === 'wss:' ? 'https' : 'http'}://${u.host}${u.pathname.replace(/\/$/, '')}/bug`; } catch { return `http://${location.hostname}:8081/bug`; }
}

/** Sends the report; resolves to a human-readable status line. Falls back to localStorage. */
export async function sendReport(url: string, report: BugReport): Promise<string> {
  try {
    const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(report), signal: AbortSignal.timeout(4000) });
    if (res.ok) { const j = await res.json().catch(() => ({})); return `Sent — thanks! (${j.file ?? 'saved on the server'})`; }
    throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    try {
      const key = 'sprawl.bugs';
      const list = JSON.parse(localStorage.getItem(key) || '[]') as BugReport[];
      list.push(report); localStorage.setItem(key, JSON.stringify(list.slice(-50)));
      return `No server reachable (${(e as Error).message}) — kept in this browser (${list.length} pending). Run \`npm run server\` and reports go there.`;
    } catch { return `Could not send (${(e as Error).message})`; }
  }
}

/** Reports kept in the browser because no server was reachable. */
export function pendingReports(): BugReport[] {
  try { return JSON.parse(localStorage.getItem('sprawl.bugs') || '[]'); } catch { return []; }
}
